import { Tooltip } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import TabContainer from "~/components/Document/tables/common/TabContainer";
import styles from "./structuredDiffViewer.module.scss";

interface StructuredDiffViewerProps {
  originalContent: string;
  updatedContent: string;
  documentType: string;
  structuredDiff?: StructuredDiffPayload | null;
  onValidityChange?: (isValid: boolean) => void;
}

export type DiffState = "unchanged" | "changed" | "added" | "removed";

export interface CellDiff {
  value: string;
  state: DiffState;
  oldValue?: string;
}

export interface RowDiff {
  cells: CellDiff[];
  state: DiffState;
}

export interface TableDiff {
  key: string;
  label: string;
  headers: string[];
  rows: RowDiff[];
}

export interface StructuredDiffPayload {
  documentType: string;
  tables: TableDiff[];
}

interface AppDesignContent {
  appTable: { headers: string[]; rows: string[][] };
  connectorTable: { headers: string[]; rows: string[][] };
  functionTable: { headers: string[]; rows: string[][] };
}

interface FieldDesignApp {
  app_id: string;
  app_name: string;
  properties: Array<{
    label?: string;
    required?: { rows: string[][] };
    [key: string]: unknown;
  }>;
}

interface FieldDesignContent {
  apps: FieldDesignApp[];
}

interface CustomizeSpecContent {
  headers: string[];
  rows: string[][];
}

const HEADER_LABEL_MAP: Record<string, string> = {
  app_id: "アプリID",
  app_name: "アプリ名",
  description: "説明",
  connector_id: "連携ID",
  sid: "データ元アプリID",
  tid: "連携先アプリID",
  relationship: "連携方法",
  keyfield: "キーフィールド",
  referencefield: "参照フィールド",
  rid: "機能ID",
  name: "機能",
  how: "実装方法",
  label: "フィールド名",
  code: "フィールドコード",
  type: "タイプ",
};

function isStructuredDiffPayload(
  payload: StructuredDiffPayload | null | undefined,
  documentType: string,
): payload is StructuredDiffPayload {
  if (!payload || payload.documentType !== documentType || !Array.isArray(payload.tables)) {
    return false;
  }

  return payload.tables.every(
    (table) =>
      typeof table.key === "string" &&
      typeof table.label === "string" &&
      Array.isArray(table.headers) &&
      Array.isArray(table.rows),
  );
}

function hasTableDataShape(obj: unknown): boolean {
  if (typeof obj !== "object" || obj === null) return false;
  const table = obj as Record<string, unknown>;
  return Array.isArray(table["headers"]) && Array.isArray(table["rows"]);
}

function parseAppDesign(content: string): AppDesignContent | null {
  try {
    const parsed = JSON.parse(content);
    if (typeof parsed !== "object" || parsed === null) return null;

    const record = parsed as Record<string, unknown>;
    const appTable = record["appTable"] ?? record["app_table"];
    const connectorTable = record["connectorTable"] ?? record["connector_table"];
    const functionTable = record["functionTable"] ?? record["function_table"];

    if (
      hasTableDataShape(appTable) &&
      hasTableDataShape(connectorTable) &&
      hasTableDataShape(functionTable)
    ) {
      return {
        appTable: appTable as { headers: string[]; rows: string[][] },
        connectorTable: connectorTable as { headers: string[]; rows: string[][] },
        functionTable: functionTable as { headers: string[]; rows: string[][] },
      };
    }

    const tableContainer = record["テーブル一覧"] ?? record["tables"];
    if (typeof tableContainer !== "object" || tableContainer === null) return null;
    const tables = tableContainer as Record<string, unknown>;
    const app = tables["アプリテーブル"];
    const connector = tables["アプリ間連携テーブル"];
    const func = tables["要求機能テーブル"];

    if (!hasTableDataShape(app) || !hasTableDataShape(connector) || !hasTableDataShape(func)) {
      return null;
    }

    return {
      appTable: app as { headers: string[]; rows: string[][] },
      connectorTable: connector as { headers: string[]; rows: string[][] },
      functionTable: func as { headers: string[]; rows: string[][] },
    };
  } catch {
    return null;
  }
}

function parseFieldDesign(content: string): FieldDesignContent | null {
  try {
    const parsed = JSON.parse(content);
    if (typeof parsed !== "object" || parsed === null) return null;
    const record = parsed as Record<string, unknown>;
    return Array.isArray(record["apps"]) ? { apps: record["apps"] as FieldDesignApp[] } : null;
  } catch {
    return null;
  }
}

function parseCustomizeSpec(content: string): CustomizeSpecContent | null {
  try {
    const parsed = JSON.parse(content);
    if (typeof parsed !== "object" || parsed === null) return null;
    const record = parsed as Record<string, unknown>;
    if (!Array.isArray(record["headers"]) || !Array.isArray(record["rows"])) return null;
    return {
      headers: record["headers"] as string[],
      rows: record["rows"] as string[][],
    };
  } catch {
    return null;
  }
}

function compareTable(
  _originalHeaders: string[],
  originalRows: string[][],
  updatedHeaders: string[],
  updatedRows: string[][],
): { headers: string[]; rows: RowDiff[] } {
  const headers = updatedHeaders;
  const rows: RowDiff[] = [];
  const maxLength = Math.max(originalRows.length, updatedRows.length);

  for (let i = 0; i < maxLength; i++) {
    const originalRow = originalRows[i];
    const updatedRow = updatedRows[i];

    if (!originalRow && updatedRow) {
      rows.push({
        cells: updatedRow.map((value) => ({ value, state: "added" })),
        state: "added",
      });
      continue;
    }

    if (originalRow && !updatedRow) {
      rows.push({
        cells: originalRow.map((value) => ({ value, state: "removed" })),
        state: "removed",
      });
      continue;
    }

    if (originalRow && updatedRow) {
      const cells: RowDiff["cells"] = [];
      let rowHasChanges = false;

      for (let j = 0; j < headers.length; j++) {
        const originalValue = originalRow[j] ?? "";
        const updatedValue = updatedRow[j] ?? "";
        if (originalValue !== updatedValue) {
          cells.push({ value: updatedValue, oldValue: originalValue, state: "changed" });
          rowHasChanges = true;
        } else {
          cells.push({ value: updatedValue, state: "unchanged" });
        }
      }

      rows.push({ cells, state: rowHasChanges ? "changed" : "unchanged" });
    }
  }

  return { headers, rows };
}

function compareAppDesign(original: AppDesignContent, updated: AppDesignContent): TableDiff[] {
  return [
    {
      key: "appTable",
      label: "作成するアプリ",
      ...compareTable(
        original.appTable.headers,
        original.appTable.rows,
        updated.appTable.headers,
        updated.appTable.rows,
      ),
    },
    {
      key: "connectorTable",
      label: "アプリ間の連携",
      ...compareTable(
        original.connectorTable.headers,
        original.connectorTable.rows,
        updated.connectorTable.headers,
        updated.connectorTable.rows,
      ),
    },
    {
      key: "functionTable",
      label: "アプリ毎の機能",
      ...compareTable(
        original.functionTable.headers,
        original.functionTable.rows,
        updated.functionTable.headers,
        updated.functionTable.rows,
      ),
    },
  ];
}

function buildFieldDesignRows(app: FieldDesignApp): string[][] {
  return (app.properties ?? []).map((property) => {
    const prop = property as Record<string, unknown>;
    const code = property.required?.rows?.[0]?.[1] ?? (prop["フィールドコード"] as string) ?? "";
    const type = property.required?.rows?.[0]?.[3] ?? (prop["フィールドタイプ"] as string) ?? "";
    const label = property.label ?? (prop["フィールド名"] as string) ?? "";
    return [label, code, type];
  });
}

function compareFieldDesign(
  original: FieldDesignContent,
  updated: FieldDesignContent,
): TableDiff[] {
  const headers = ["label", "code", "type"];
  const originalAppsMap = new Map(original.apps.map((app) => [app.app_id, app]));
  const tables: TableDiff[] = [];

  for (const updatedApp of updated.apps) {
    const originalApp = originalAppsMap.get(updatedApp.app_id);
    tables.push({
      key: updatedApp.app_id,
      label: updatedApp.app_name,
      ...compareTable(
        headers,
        originalApp ? buildFieldDesignRows(originalApp) : [],
        headers,
        buildFieldDesignRows(updatedApp),
      ),
    });
  }

  for (const originalApp of original.apps) {
    if (updated.apps.some((app) => app.app_id === originalApp.app_id)) continue;
    tables.push({
      key: originalApp.app_id,
      label: originalApp.app_name,
      ...compareTable(headers, buildFieldDesignRows(originalApp), headers, []),
    });
  }

  return tables;
}

function compareCustomizeSpec(
  original: CustomizeSpecContent,
  updated: CustomizeSpecContent,
): TableDiff[] {
  return [
    {
      key: "customize-specification",
      label: "カスタマイズ一覧",
      ...compareTable(original.headers, original.rows, updated.headers, updated.rows),
    },
  ];
}

export const StructuredDiffViewer: React.FC<StructuredDiffViewerProps> = ({
  originalContent,
  updatedContent,
  documentType,
  structuredDiff,
  onValidityChange,
}) => {
  const [selectedTab, setSelectedTab] = useState(0);

  const tableDiffs = useMemo((): TableDiff[] | null => {
    if (isStructuredDiffPayload(structuredDiff, documentType)) {
      return structuredDiff.tables;
    }

    switch (documentType) {
      case "app-design": {
        const original = parseAppDesign(originalContent);
        const updated = parseAppDesign(updatedContent);
        return original && updated ? compareAppDesign(original, updated) : null;
      }
      case "field-design": {
        const original = parseFieldDesign(originalContent);
        const updated = parseFieldDesign(updatedContent);
        return original && updated ? compareFieldDesign(original, updated) : null;
      }
      case "customize-specification": {
        const original = parseCustomizeSpec(originalContent);
        const updated = parseCustomizeSpec(updatedContent);
        return original && updated ? compareCustomizeSpec(original, updated) : null;
      }
      default:
        return null;
    }
  }, [documentType, originalContent, structuredDiff, updatedContent]);

  useEffect(() => {
    onValidityChange?.(tableDiffs !== null);
  }, [onValidityChange, tableDiffs]);

  useEffect(() => {
    setSelectedTab(0);
  }, [tableDiffs]);

  const changeCounts = useMemo(() => {
    if (!tableDiffs) return [];
    return tableDiffs.map((table) => table.rows.filter((row) => row.state !== "unchanged").length);
  }, [tableDiffs]);

  if (!tableDiffs) {
    return (
      <div className={styles["structuredDiffContainer"]}>
        <div className={styles["noChange"]}>差分を表示できません。</div>
      </div>
    );
  }

  const hasAnyChange = tableDiffs.some((table) =>
    table.rows.some((row) => row.state !== "unchanged"),
  );

  if (!hasAnyChange) {
    return (
      <div className={styles["structuredDiffContainer"]}>
        <div className={styles["noChange"]}>変更はありません。</div>
      </div>
    );
  }

  const renderDiffTable = (table: TableDiff, showLabel: boolean) => (
    <div className={styles["diffTableSection"]}>
      {showLabel && <h4 className={styles["tableLabel"]}>{table.label}</h4>}
      <div className={styles["tableWrapper"]}>
        <table className={styles["diffTable"]}>
          <thead>
            <tr>
              {table.headers.map((header, index) => (
                <th key={`header-${index.toString()}`}>{HEADER_LABEL_MAP[header] ?? header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr
                key={`row-${rowIndex.toString()}`}
                className={
                  row.state === "added"
                    ? styles["rowAdded"]
                    : row.state === "removed"
                      ? styles["rowRemoved"]
                      : ""
                }
              >
                {row.cells.map((cell, cellIndex) => (
                  <td
                    key={`cell-${rowIndex.toString()}-${cellIndex.toString()}`}
                    className={cell.state === "changed" ? styles["cellChanged"] : ""}
                  >
                    {cell.state === "changed" && cell.oldValue !== undefined ? (
                      <Tooltip title={`変更前: ${cell.oldValue}`} placement="top" arrow>
                        <span className={styles["changedValue"]}>{cell.value}</span>
                      </Tooltip>
                    ) : (
                      cell.value
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (tableDiffs.length === 1) {
    return (
      <div className={styles["structuredDiffContainer"]}>
        {renderDiffTable(tableDiffs[0]!, true)}
      </div>
    );
  }

  const tabs = tableDiffs.map((table, index) => ({
    key: table.key,
    label: `${table.label}${changeCounts[index] ? `（${changeCounts[index]}件変更）` : ""}`,
    content: renderDiffTable(table, false),
  }));

  return (
    <div className={styles["structuredDiffContainer"]}>
      <TabContainer mode="full" tabs={tabs} selectedIndex={selectedTab} onSelect={setSelectedTab} />
    </div>
  );
};
